import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';

/**
 * Sunmi Printer Service
 * Handles printing on Sunmi POS devices (V2, V2 Pro, etc.)
 * Supports both Sunmi native printer API and fallback methods
 */
@Injectable({
  providedIn: 'root'
})
export class SunmiPrinterService {

  private sunmiPrinter: any = null;
  private isPrinterAvailable = false;

  constructor() {
    this.initializePrinter();
  }

  /**
   * Initialize Sunmi printer
   * Checks if window.SunmiPrinter is available (WebView integration)
   */
  private initializePrinter(): void {
    if (typeof window !== 'undefined' && (window as any).SunmiPrinter) {
      this.sunmiPrinter = (window as any).SunmiPrinter;
      this.isPrinterAvailable = true;

      // Initialize printer
      try {
        this.sunmiPrinter.printerInit();
      } catch (error) {
        console.error('Failed to initialize Sunmi printer:', error);
        this.isPrinterAvailable = false;
      }
    } else {
      console.warn('Sunmi Printer API not available - using fallback method');
    }
  }

  /**
   * Check if Sunmi printer is available
   */
  isSunmiPrinterAvailable(): boolean {
    return this.isPrinterAvailable;
  }

  /**
   * Print text using Sunmi native API
   */
  printText(text: string): Observable<boolean> {
    if (!this.isPrinterAvailable || !this.sunmiPrinter) {
      return throwError(() => new Error('Sunmi printer not available'));
    }

    try {
      this.sunmiPrinter.printText(text);
      return of(true);
    } catch (error) {
      console.error('Sunmi print error:', error);
      return throwError(() => error);
    }
  }

  /**
   * Print image using Sunmi native API
   * @param base64Image - Base64 encoded image string
   */
  printImage(base64Image: string): Observable<boolean> {
    if (!this.isPrinterAvailable || !this.sunmiPrinter) {
      return throwError(() => new Error('Sunmi printer not available'));
    }

    try {
      // Remove data URI prefix if present
      const imageData = base64Image.replace(/^data:image\/[a-z]+;base64,/, '');
      this.sunmiPrinter.printBitmap(imageData);
      return of(true);
    } catch (error) {
      console.error('Sunmi print image error:', error);
      return throwError(() => error);
    }
  }

  /**
   * Print multiple images
   */
  printImages(base64Images: string[]): Observable<boolean> {
    if (!this.isPrinterAvailable || !this.sunmiPrinter) {
      return throwError(() => new Error('Sunmi printer not available'));
    }

    try {
      base64Images.forEach(image => {
        const imageData = image.replace(/^data:image\/[a-z]+;base64,/, '');
        this.sunmiPrinter.printBitmap(imageData);
      });
      // Add line wraps between tickets
      this.sunmiPrinter.lineWrap(3);
      return of(true);
    } catch (error) {
      console.error('Sunmi print images error:', error);
      return throwError(() => error);
    }
  }

  /**
   * Add line wraps
   */
  lineWrap(lines: number = 3): void {
    if (this.isPrinterAvailable && this.sunmiPrinter) {
      try {
        this.sunmiPrinter.lineWrap(lines);
      } catch (error) {
        console.error('Sunmi line wrap error:', error);
      }
    }
  }

  /**
   * Cut paper (if supported)
   */
  cutPaper(): void {
    if (this.isPrinterAvailable && this.sunmiPrinter && this.sunmiPrinter.cutPaper) {
      try {
        this.sunmiPrinter.cutPaper();
      } catch (error) {
        console.error('Sunmi cut paper error:', error);
      }
    }
  }

  /**
   * Print receipt using Sunmi API
   * @param ticketImages - Array of base64 ticket images
   * @param ticketCount - Number of tickets to print
   */
  printReceipt(ticketImages: string[], ticketCount: number): Observable<boolean> {
    if (!this.isPrinterAvailable) {
      return throwError(() => new Error('Sunmi printer not available'));
    }

    return from(
      new Promise<boolean>((resolve, reject) => {
        try {

          // Print each ticket image
          ticketImages.forEach((image, index) => {
            const imageData = image.replace(/^data:image\/[a-z]+;base64,/, '');
            this.sunmiPrinter.printBitmap(imageData);

            // Add spacing between tickets (except after last one)
            if (index < ticketImages.length - 1) {
              this.sunmiPrinter.lineWrap(2);
            }
          });

          // Final line wrap and cut
          this.sunmiPrinter.lineWrap(3);
          if (this.sunmiPrinter.cutPaper) {
            this.sunmiPrinter.cutPaper();
          }

          resolve(true);
        } catch (error) {
          console.error('Sunmi receipt print error:', error);
          reject(error);
        }
      })
    );
  }

  /**
   * Get printer status
   */
  getPrinterStatus(): string {
    if (!this.isPrinterAvailable || !this.sunmiPrinter) {
      return 'unavailable';
    }

    try {
      if (this.sunmiPrinter.getPrinterStatus) {
        return this.sunmiPrinter.getPrinterStatus();
      }
      return 'unknown';
    } catch (error) {
      console.error('Failed to get printer status:', error);
      return 'error';
    }
  }
}
